import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"

import { WorkflowStatusBadge, deriveWorkflowStatus } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface TimeEntry {
  id: number
  entry_date: string | null
  user_name: string | null
  service_type: string | null
  hours: number | null
  entry_class: "Block" | "Actual"
  mileage: number
  employee_pay?: number
  billable_amount?: number
  submitted: boolean
  approved: boolean
  paid: boolean
  billed: boolean
  can_edit: boolean
  can_approve: boolean
  can_delete: boolean
}

interface Summary {
  total_hours: number
  total_mileage: number
  total_employee_pay?: number
  total_billable?: number
  pending_approval: number
  pending_payment: number
}

interface FilterOption {
  id: number
  name: string
}

interface Filters {
  start_date: string | null
  end_date: string | null
  user_id: number | null
  service_item_type_id: number | null
}

interface FilterOptions {
  employees: FilterOption[]
  service_item_types: FilterOption[]
}

interface Props {
  case_record: CaseRecord
  time_entries: TimeEntry[]
  summary: Summary
  can_view_financials: boolean
  filters: Filters
  filter_options: FilterOptions
}

function formatHours(hours: number | null): string {
  if (!hours || hours <= 0) return "-"
  const totalMin = Math.round(hours * 60)
  const h = Math.floor(totalMin / 60)
  const m = totalMin % 60
  if (h > 0 && m > 0) return `${h}h ${m}m`
  if (h > 0) return `${h}h`
  return `${m}m`
}

export default function Index({
  case_record,
  time_entries,
  summary,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  can_view_financials: _can_view_financials,
  filters,
  filter_options,
}: Props) {
  const [startDate, setStartDate] = useState(filters.start_date ?? "")
  const [endDate, setEndDate] = useState(filters.end_date ?? "")
  const [userId, setUserId] = useState(filters.user_id?.toString() ?? "")
  const [serviceItemTypeId, setServiceItemTypeId] = useState(
    filters.service_item_type_id?.toString() ?? "",
  )

  const applyFilters = () => {
    const params: Record<string, string> = {}
    if (startDate) params.start_date = startDate
    if (endDate) params.end_date = endDate
    if (userId) params.user_id = userId
    if (serviceItemTypeId) params.service_item_type_id = serviceItemTypeId
    router.get(`/cases/${case_record.id}/time_entries`, params, {
      preserveState: true,
    })
  }

  const clearFilters = () => {
    setStartDate("")
    setEndDate("")
    setUserId("")
    setServiceItemTypeId("")
    router.get(`/cases/${case_record.id}/time_entries`)
  }

  const hasActiveFilters = startDate || endDate || userId || serviceItemTypeId
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Time Entries", href: `/cases/${case_record.id}/time_entries` },
  ]

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this time entry?")) {
      router.delete(`/cases/${case_record.id}/time_entries/${id}`)
    }
  }

  const handleApprove = (id: number) => {
    router.patch(`/cases/${case_record.id}/time_entries/${id}/approve`)
  }

  const statusBadge = (entry: TimeEntry) => (
    <WorkflowStatusBadge status={deriveWorkflowStatus(entry)} />
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Time Entries - ${case_record.case_number}`} />

      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <h1 className="text-2xl font-semibold">
            Time Entries for {case_record.case_number}
          </h1>
          <Link
            href={`/cases/${case_record.id}/time_entries/new`}
            className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white"
          >
            Add Time Entry
          </Link>
        </div>

        {/* Summary Cards */}
        <div className="mb-6 grid grid-cols-2 gap-4">
          <div className="rounded-lg bg-white p-4 shadow">
            <div className="text-hpi-med-gray text-sm">Total Hours</div>
            <div className="text-2xl font-bold">
              {summary.total_hours.toFixed(2)}
            </div>
          </div>
          <div className="rounded-lg bg-white p-4 shadow">
            <div className="text-hpi-med-gray text-sm">Total Mileage</div>
            <div className="text-2xl font-bold">{summary.total_mileage}</div>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-4 rounded-lg bg-white p-4 shadow">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="text-hpi-med-gray mb-1 block text-xs">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
              />
            </div>
            {filter_options.employees.length > 1 && (
              <div>
                <label className="text-hpi-med-gray mb-1 block text-xs">
                  Employee
                </label>
                <select
                  value={userId}
                  onChange={(e) => setUserId(e.target.value)}
                  className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
                >
                  <option value="">All Employees</option>
                  {filter_options.employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {filter_options.service_item_types.length > 1 && (
              <div>
                <label className="text-hpi-med-gray mb-1 block text-xs">
                  Type
                </label>
                <select
                  value={serviceItemTypeId}
                  onChange={(e) => setServiceItemTypeId(e.target.value)}
                  className="border-hpi-light-gray rounded border px-3 py-1.5 text-sm"
                >
                  <option value="">All Types</option>
                  {filter_options.service_item_types.map((sit) => (
                    <option key={sit.id} value={sit.id}>
                      {sit.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="flex gap-2">
              <button
                onClick={applyFilters}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-1.5 text-sm text-white"
              >
                Filter
              </button>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-hpi-med-gray hover:text-hpi-dark-gray rounded border border-gray-300 px-4 py-1.5 text-sm"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>

        {summary.pending_approval > 0 && (
          <div className="bg-hpi-yellow/20 text-hpi-dark-gray mb-4 rounded p-3">
            {summary.pending_approval} time{" "}
            {summary.pending_approval === 1 ? "entry" : "entries"} pending
            approval
          </div>
        )}

        {time_entries.length > 0 ? (
          <table className="border-hpi-light-gray w-full border-collapse border text-sm">
            <thead>
              <tr className="bg-hpi-pale-gray">
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Date
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Employee
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Service
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-right">
                  Hours
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-right">
                  Mileage
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-center">
                  Status
                </th>
                <th className="border-hpi-light-gray border px-4 py-2 text-left">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {time_entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-hpi-pale-blue">
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {entry.entry_date}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {entry.user_name}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    {entry.service_type}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-right">
                    {formatHours(entry.hours)}
                    {entry.entry_class === "Block" && (
                      <span className="bg-hpi-pale-gray text-hpi-med-gray ml-1 rounded px-1.5 py-0.5 text-xs">
                        Block
                      </span>
                    )}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-right">
                    {entry.mileage}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2 text-center">
                    {statusBadge(entry)}
                  </td>
                  <td className="border-hpi-light-gray border px-4 py-2">
                    <Link
                      href={`/cases/${case_record.id}/time_entries/${entry.id}`}
                      className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                    >
                      View
                    </Link>
                    {entry.can_edit && (
                      <Link
                        href={`/cases/${case_record.id}/time_entries/${entry.id}/edit`}
                        className="text-hpi-active-blue hover:text-hpi-active-blue mr-2"
                      >
                        Edit
                      </Link>
                    )}
                    {entry.can_approve && !entry.approved && (
                      <button
                        onClick={() => handleApprove(entry.id)}
                        className="text-hpi-dark-green hover:text-hpi-dark-green mr-2"
                      >
                        Approve
                      </button>
                    )}
                    {entry.can_delete && (
                      <button
                        onClick={() => handleDelete(entry.id)}
                        className="text-hpi-red hover:text-hpi-red"
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="text-hpi-med-gray">No time entries found.</p>
        )}

        <div className="mt-4">
          <Link
            href={`/cases/${case_record.id}`}
            className="text-hpi-active-blue hover:text-hpi-active-blue"
          >
            Back to Case
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
